import { AnimatePresence, motion } from "framer-motion";
import { Heart, Minus, Plus, ShoppingBag, Trash2, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { BOX_GREEN, BOX_PINK } from "@/data/content";

const inr = (amount) => `₹${Number(amount).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function CartDrawer() {
  const { cart, drawerOpen, setDrawerOpen, updateLine, removeLine, busy } = useCart();
  const lines = cart?.lines?.edges ?? [];

  return (
    <AnimatePresence>
      {drawerOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setDrawerOpen(false)}
            className="fixed inset-0 bg-[#2C221E]/50 backdrop-blur-sm z-[70]"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 32 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-[#FAF7F2] z-[80] flex flex-col shadow-2xl"
          >
            <div className="flex items-center justify-between px-7 py-6 border-b border-[#2C221E]/10">
              <h2 className="font-serif text-2xl font-semibold text-[#2C221E] inline-flex items-center gap-3">
                Your Basket
                {cart?.totalQuantity > 0 && (
                  <span className="font-mono text-xs bg-[#F8C8DC] text-[#6A2C3E] rounded-full px-3 py-1">
                    {cart.totalQuantity}
                  </span>
                )}
              </h2>
              <button
                data-testid="cart-close-button"
                onClick={() => setDrawerOpen(false)}
                className="p-2 text-[#6E5E57] hover:text-[#2C221E] transition-colors"
                aria-label="Close basket"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-7 py-6">
              {lines.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center gap-4">
                  <ShoppingBag className="w-10 h-10 text-[#C86D51]" />
                  <p className="font-serif text-xl text-[#2C221E]">Your basket is empty</p>
                  <p className="text-sm text-[#6E5E57] max-w-[240px]">
                    Warm cakes await — add a mix or two and bake something sweet.
                  </p>
                  <button
                    data-testid="cart-browse-button"
                    onClick={() => setDrawerOpen(false)}
                    className="mt-2 rounded-full border border-[#2C221E]/25 px-6 py-3 text-sm font-medium text-[#2C221E] hover:bg-[#2C221E] hover:text-[#FAF7F2] transition-all duration-300"
                  >
                    Browse the mixes
                  </button>
                </div>
              ) : (
                <ul className="space-y-5">
                  {lines.map(({ node }) => {
                    const isVanilla = node.merchandise.product.handle.includes("vanilla");
                    return (
                      <li
                        key={node.id}
                        data-testid={`cart-line-${isVanilla ? "vanilla" : "chocolate"}`}
                        className="flex gap-4 rounded-3xl border border-[#2C221E]/10 bg-white/60 p-4"
                      >
                        <div className="w-16 h-16 rounded-2xl overflow-hidden shrink-0 spotlight-frame">
                          <img
                            src={node.merchandise.image?.url || (isVanilla ? BOX_GREEN : BOX_PINK)}
                            alt={node.merchandise.product.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-serif font-semibold text-[#2C221E] leading-snug text-sm">
                            {node.merchandise.product.title}
                          </p>
                          <p className="font-mono text-xs text-[#6E5E57] mt-1">
                            {inr(node.merchandise.price.amount)} each
                          </p>
                          <div className="mt-2.5 flex items-center gap-3">
                            <div className="inline-flex items-center rounded-full border border-[#2C221E]/15">
                              <button
                                data-testid={`cart-qty-minus-${node.id}`}
                                onClick={() => updateLine(node.id, node.quantity - 1)}
                                disabled={busy}
                                className="p-1.5 px-2.5 text-[#2C221E] hover:text-[#6A2C3E] disabled:opacity-40"
                                aria-label="Decrease quantity"
                              >
                                <Minus className="w-3.5 h-3.5" />
                              </button>
                              <span className="font-mono text-sm w-6 text-center">{node.quantity}</span>
                              <button
                                data-testid={`cart-qty-plus-${node.id}`}
                                onClick={() => updateLine(node.id, node.quantity + 1)}
                                disabled={busy}
                                className="p-1.5 px-2.5 text-[#2C221E] hover:text-[#6A2C3E] disabled:opacity-40"
                                aria-label="Increase quantity"
                              >
                                <Plus className="w-3.5 h-3.5" />
                              </button>
                            </div>
                            <button
                              data-testid={`cart-remove-${node.id}`}
                              onClick={() => removeLine(node.id)}
                              disabled={busy}
                              className="p-1.5 text-[#6E5E57] hover:text-[#6A2C3E] transition-colors disabled:opacity-40"
                              aria-label="Remove item"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {lines.length > 0 && (
              <div className="border-t border-[#2C221E]/10 px-7 py-6 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#6E5E57]">Subtotal</span>
                  <span data-testid="cart-subtotal" className="font-serif text-2xl font-semibold text-[#2C221E]">
                    {inr(cart.cost.subtotalAmount.amount)}
                  </span>
                </div>
                <button
                  data-testid="cart-checkout-button"
                  onClick={() => {
                    const url = cart.checkoutUrl.replace(
                      /^https?:\/\/[^/]+/,
                      `https://${process.env.REACT_APP_SHOPIFY_STORE_DOMAIN}`
                    );
                    window.open(url, "_blank", "noopener");
                  }}
                  className="w-full rounded-full bg-[#2C221E] text-[#FAF7F2] px-8 py-4 text-sm font-medium hover:bg-[#6A2C3E] hover:scale-[1.01] active:scale-[0.99] transition-all duration-300"
                >
                  Checkout securely with Shopify
                </button>
                <p className="text-center font-mono text-[10px] uppercase tracking-[0.2em] text-[#6E5E57] inline-flex items-center justify-center gap-1.5 w-full">
                  UPI · Cards · COD <Heart className="w-3 h-3 text-[#C86D51] fill-current" /> Ships across India
                </p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
